import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Logo } from '../App';
import { useAuth } from '../auth/AuthProvider';
import { ROLE_LABELS } from '@pietrochip/shared';
import { useConnection } from '../lib/ConnectionProvider';
import { AudioToggle } from './AudioToggle';

export function Shell({ children }: { children: ReactNode }) {
  const { user, signOut, freeAccess } = useAuth();
  const { online } = useConnection();

  return (
    <div className="shell">
      <header className="shell__bar">
        {/*
          Il contenuto sta dentro un involucro centrato con la stessa colonna di
          `.shell__main`: è quello che lo fa combaciare con i riquadri sotto.
          Il fondo scuro e il filo di marca restano invece sulla barra, a tutta
          larghezza.
        */}
        <div className="shell__bar-inner">
          <Link to="/" className="shell__brand">
            <Logo size={26} />
            <span>PietroChip</span>
          </Link>

          <nav className="row shell__nav" style={{ gap: 4 }} aria-label="Sezioni">
            <NavTab to="/" label="Eventi" />
            {user?.role === 'admin' && <NavTab to="/utenti" label="Utenti" />}
          </nav>

          <span className="shell__spacer" />

          {/* I segnali acustici sono una preferenza del dispositivo: stanno in
              barra, sempre a portata, senza aprire nessun pannello. */}
          <AudioToggle />

          {/*
            Un puntino quando va tutto bene, una parola quando non va: il resto
            dell'informazione la dà l'avviso che copre lo schermo.
          */}
          <span
            className="row shell__status"
            style={{ gap: 6, fontSize: '0.8125rem', opacity: 0.85 }}
            title={online ? 'Connesso al server' : 'Connessione assente'}
          >
            <span
              aria-hidden="true"
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: online ? '#35C46F' : '#E0A33C',
                display: 'inline-block',
              }}
            />
            {!online && <span className="shell__status-label">Offline</span>}
          </span>

          {/*
            Con l'accesso libero non c'è un nome da mostrare — quello in barra
            sarebbe l'utente di riserva, non chi ha il tablet in mano — e «Esci»
            sarebbe un pulsante che si preme e non succede niente, perché la
            richiesta successiva rientra da sola. Meglio dirlo che fingere.
          */}
          {freeAccess ? (
            <span
              className="shell__free btn btn--sm btn--quiet"
              style={{ color: 'inherit', cursor: 'default' }}
              title="L’accesso è libero: chiunque abbia l’indirizzo può usare l’app."
            >
              Accesso libero
            </span>
          ) : (
            <>
              {/* Il nome è l'unica larghezza imprevedibile della barra: tronca
                  lui, invece di mandare la riga fuori dallo schermo. */}
              <Link
                to="/account"
                className="shell__user btn btn--sm btn--quiet"
                style={{ color: 'inherit' }}
              >
                <span className="truncate">{user?.name}</span>
              </Link>

              <button
                type="button"
                className="shell__signout btn btn--sm btn--quiet"
                style={{ color: 'inherit' }}
                onClick={() => void signOut()}
              >
                Esci
              </button>
            </>
          )}

          <span className="sr-only">{user ? ROLE_LABELS[user.role] : ''}</span>
        </div>
      </header>

      <main className="shell__main">{children}</main>
    </div>
  );
}

function NavTab({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className="btn btn--sm btn--quiet"
      style={({ isActive }) => ({
        color: 'inherit',
        textDecoration: 'none',
        borderColor: isActive ? 'currentColor' : 'transparent',
      })}
    >
      {label}
    </NavLink>
  );
}
